import { Link, createFileRoute, type ErrorComponentProps } from '@tanstack/react-router'

import RouteErrorPage from '@/components/layout/RouteErrorPage'
import ScreenLayout from '@/components/layout/ScreenLayout'
import EmptyState from '@/components/ui/EmptyState'
import { stateActionClass } from '@/components/ui/empty-state-variants'
import SectionHead from '@/components/ui/SectionHead'
import SeeAllLink from '@/components/ui/SeeAllLink'
import TitleRow from '@/components/ui/TitleRow'
import { LEARN_PAGE } from '@/screens/courses/components/learn/learn-page'
import { useSuspenseCertificates } from '@/features/courses/api/queries'
import CertificateCard from '@/screens/courses/components/certificates/CertificateCard'
import { CARD_GRID } from '@/screens/courses/components/certificates/certificates-box'
import CertificatesSkeleton from '@/screens/courses/components/certificates/CertificatesSkeleton'
import MyCourseCard from '@/screens/courses/components/shelf/MyCourseCard'
import { SHELF_GRID, SHELF_MAX } from '@/screens/courses/components/shelf/shelf-box'
import { loadCertificates } from '@/screens/courses/CertificatesScreen.loader'

const TITLE = 'Certificates'

/**
 * The section tab strip names this page from `lg` up, so its `h1` is
 * screen-reader only there; below `lg` there is no strip and it shows. The
 * intro line under it shows at every width (rest:ln, the mock's `ln-intro`).
 */
const HEADER = (
  <TitleRow
    title={TITLE}
    subtitle="Issued when you finish every lecture in a course. Each one carries a code anyone can check."
  />
)

function CertificatesPending() {
  return (
    <ScreenLayout
      {...LEARN_PAGE}
      pending
      pendingLabel="Loading your certificates…"
      header={HEADER}
      pageTitle={TITLE}
    >
      <CertificatesSkeleton />
    </ScreenLayout>
  )
}

function CertificatesError(props: ErrorComponentProps) {
  return (
    <RouteErrorPage {...props} fullWidth header={HEADER} title="Your certificates did not load" />
  )
}

export const Route = createFileRoute('/_app/courses/certificates')({
  loader: loadCertificates,
  pendingComponent: CertificatesPending,
  errorComponent: CertificatesError,
  component: CertificatesScreen,
})

/**
 * Learn · Certificates — every certificate the member has earned, and the
 * courses one certificate away ("Almost there", with "My learning" one link
 * on for the rest).
 *
 * One suspense read (`certificatesQueryOptions`, this route's only data): no
 * search params, nothing paged, so there is nothing here `keepPreviousData`
 * or a search-only render needs to worry about.
 */
function CertificatesScreen() {
  const { data } = useSuspenseCertificates()
  // mm-be's `close_to_one` is every started-but-unfinished course, whether or
  // not it awards a certificate — "close to a certificate" only makes sense
  // for the ones that do.
  const closeToOne = data.closeToOne.filter((row) => row.course.hasCertificate)

  return (
    <ScreenLayout {...LEARN_PAGE} header={HEADER} pageTitle={TITLE}>
      <div className="flex flex-col gap-3">
        <SectionHead title={`Earned (${String(data.issued.length)})`} size="card" />
        {data.issued.length === 0 ? (
          <EmptyState
            size="section"
            framed
            title="Finish a course with a certificate to earn one"
            description="Courses that award a certificate are marked on the course page."
            action={
              <Link to="/courses" className={stateActionClass('section')}>
                Browse courses
              </Link>
            }
          />
        ) : (
          <div className={CARD_GRID}>
            {data.issued.map((certificate) => (
              <CertificateCard
                key={certificate.code ?? `${String(certificate.course.id)}-${certificate.issuedOn}`}
                certificate={certificate}
              />
            ))}
          </div>
        )}
      </div>

      {closeToOne.length === 0 ? null : (
        <section className="flex flex-col gap-3">
          <SectionHead
            title="Almost there"
            size="card"
            action={<SeeAllLink to="/courses/my-learning">My learning</SeeAllLink>}
          />
          <div className={SHELF_GRID}>
            {closeToOne.slice(0, SHELF_MAX).map((row) => (
              <MyCourseCard key={row.course.id} row={row} />
            ))}
          </div>
        </section>
      )}
    </ScreenLayout>
  )
}
